import { useSyncExternalStore } from "react";

const LOCK_NAME = "oculera-scene-poll-leader";
const CHANNEL_NAME = "oculera-scene";

const leaderListeners = new Set<() => void>();
let isLeader = false;
let lockRequestActive = false;
let lockAbortController: AbortController | null = null;
let releaseCurrentLock: (() => void) | null = null;
let fallbackLeader = false;

type WebLocksManager = {
  request: (
    name: string,
    options: { mode: "exclusive"; signal?: AbortSignal },
    callback: () => Promise<void> | void,
  ) => Promise<void>;
};

function readLeader(): boolean {
  return isLeader;
}

function notifyLeader() {
  for (const listener of leaderListeners) listener();
}

function setLeader(nextIsLeader: boolean) {
  if (isLeader === nextIsLeader) return;
  isLeader = nextIsLeader;
  notifyLeader();
}

function getLocks(): WebLocksManager | undefined {
  const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
  if (!locks || typeof locks.request !== "function") {
    return undefined;
  }
  return locks;
}

function requestLeadership() {
  if (lockRequestActive || fallbackLeader || leaderListeners.size === 0) return;

  const locks = getLocks();
  if (!locks) {
    fallbackLeader = true;
    setLeader(true);
    return;
  }

  try {
    lockRequestActive = true;
    const controller = new AbortController();
    lockAbortController = controller;

    locks
      .request(LOCK_NAME, { mode: "exclusive", signal: controller.signal }, () => {
        lockAbortController = null;
        if (leaderListeners.size === 0) return undefined;

        setLeader(true);
        return new Promise<void>((resolve) => {
          releaseCurrentLock = resolve;
        });
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        fallbackLeader = true;
        setLeader(true);
      })
      .finally(() => {
        lockRequestActive = false;
        lockAbortController = null;
        releaseCurrentLock = null;

        if (!fallbackLeader) {
          setLeader(false);
          requestLeadership();
        }
      });
  } catch {
    lockRequestActive = false;
    lockAbortController = null;
    fallbackLeader = true;
    setLeader(true);
  }
}

function releaseLeadershipIfUnused() {
  if (leaderListeners.size > 0) return;

  if (fallbackLeader) {
    fallbackLeader = false;
    setLeader(false);
    return;
  }

  lockAbortController?.abort();
  lockAbortController = null;

  const releaseLock = releaseCurrentLock;
  releaseCurrentLock = null;
  releaseLock?.();
  setLeader(false);
}

function subscribeLeader(listener: () => void): () => void {
  leaderListeners.add(listener);
  requestLeadership();

  return () => {
    leaderListeners.delete(listener);
    releaseLeadershipIfUnused();
  };
}

function subscribeInactiveLeader(): () => void {
  return () => {};
}

function readInactiveLeader(): boolean {
  return false;
}

export function useSceneLeader(enabled = true): boolean {
  return useSyncExternalStore(
    enabled ? subscribeLeader : subscribeInactiveLeader,
    enabled ? readLeader : readInactiveLeader,
    readInactiveLeader,
  );
}

let channel: BroadcastChannel | null = null;
let channelTried = false;

function getChannel(): BroadcastChannel | null {
  if (channelTried) return channel;
  channelTried = true;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channel = null;
  }
  return channel;
}

export type BroadcastedScene = {
  pid: string | null;
  scene: string | null;
  etag: string | null;
  ts: number;
};

function isBroadcastedScene(data: unknown): data is BroadcastedScene {
  if (!data || typeof data !== "object") {
    return false;
  }

  const candidate = data as Record<string, unknown>;

  return (
    (typeof candidate.pid === "string" || candidate.pid === null) &&
    (typeof candidate.scene === "string" || candidate.scene === null) &&
    (typeof candidate.etag === "string" || candidate.etag === null) &&
    typeof candidate.ts === "number" &&
    Number.isFinite(candidate.ts)
  );
}

export function broadcastScene(
  scene: string | null,
  etag: string | null = null,
  pid: string | null = null,
): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    // BroadcastChannel does not echo to this same channel object; the polling
    // leader updates local Redux before broadcasting to follower tabs.
    ch.postMessage({ pid, scene, etag, ts: Date.now() } as BroadcastedScene);
  } catch {
    // ignore: closed channel or non-cloneable payload
  }
}

export function subscribeBroadcastedScene(
  handler: (data: BroadcastedScene) => void,
  pid: string | null = null,
): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event: MessageEvent<BroadcastedScene>) => {
    const data = event.data;
    if (!isBroadcastedScene(data)) return;
    if (pid !== null && data.pid !== pid) return;
    handler(data);
  };
  ch.addEventListener("message", listener);
  return () => ch.removeEventListener("message", listener);
}

export const __test__ = {
  readLeader,
  subscribeLeader,
};
